import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * napvila bundle picker: radio tiers that set the quantity in the product form,
 * per-unit color/size rows so a Pair or Set can mix variants, plus an optional
 * add-on checkbox (cross-sell) that is added to the cart in the same click as
 * the main product.
 *
 * The discounts themselves are automatic discounts configured in the Shopify
 * admin (they count the total quantity of the product, whatever the variants),
 * so the tiers only need to move the quantity selector, and the extra lines
 * only need to reach the cart before the main product does.
 *
 * Row #1 always mirrors the main variant picker (so the gallery follows it);
 * the other rows follow it too until the customer changes them.
 *
 * @typedef {{ id: number, options: string[], price: number, available: boolean }} Variant
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement[]} radios - One radio per tier (value = quantity).
 * @property {HTMLInputElement} [addon] - The add-on checkbox.
 * @property {HTMLElement} [units] - Wrapper of the per-unit rows.
 * @property {HTMLElement[]} [unitRows] - One row per unit (#1, #2, …).
 * @property {HTMLScriptElement} [variantsJson] - The product variants.
 *
 * @extends Component<Refs>
 */
class NapvilaBundlePickerComponent extends Component {
  requiredRefs = ['radios'];

  /** @type {boolean} */
  #bypassSubmit = false;

  /** @type {number} */
  #quantity = 1;

  /** Indexes of the rows the customer changed by hand. @type {Set<number>} */
  #touched = new Set();

  /** @type {Variant[] | null} */
  #variantsCache = null;

  /** @type {string[] | null} */
  #mainOptions = null;

  /** @type {number | null} */
  #mainPrice = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    // window, not document: Horizon's own on:submit delegate is a document capture
    // listener registered first, and this must run before it.
    window.addEventListener('submit', this.#onFormSubmit, true);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#syncFromForm();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate);
    window.removeEventListener('submit', this.#onFormSubmit, true);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  /** The product form component in the same section. */
  get #productForm() {
    const sectionId = this.dataset.sectionId;
    return (
      (sectionId && document.querySelector(`product-form-component[data-section-id="${sectionId}"]`)) ||
      this.#section?.querySelector('product-form-component') ||
      null
    );
  }

  /** The quantity selector of the product form in the same section. */
  get #quantitySelector() {
    const form = this.#productForm;
    if (!form) return null;
    return /** @type {any} */ (form).refs?.quantitySelector || form.querySelector('quantity-selector-component');
  }

  /** The main variant picker in the same section. */
  get #variantPicker() {
    const sectionId = this.dataset.sectionId;
    return (
      (sectionId && document.querySelector(`variant-picker[data-section-id="${sectionId}"]`)) ||
      this.#section?.querySelector('variant-picker') ||
      null
    );
  }

  /** @returns {Variant[]} */
  get #variants() {
    if (this.#variantsCache) return this.#variantsCache;
    try {
      this.#variantsCache = JSON.parse(this.refs.variantsJson?.textContent || '[]');
    } catch {
      this.#variantsCache = [];
    }
    return /** @type {Variant[]} */ (this.#variantsCache);
  }

  /** @returns {HTMLElement[]} */
  get #rows() {
    return this.refs.unitRows || [];
  }

  /**
   * Called from a radio: on:change="/select".
   * @param {Event} event
   */
  select(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const quantity = Number(input.value) || 1;

    const selector = /** @type {any} */ (this.#quantitySelector);
    if (selector) {
      const max = Number(selector.refs?.quantityInput?.max) || Infinity;
      const value = Math.min(quantity, max);
      selector.setValue?.(String(value));
      selector.onQuantityChange?.();
      selector.updateButtonStates?.();
    }

    this.#highlight(quantity, false);
  }

  /**
   * Called from a row's select: on:change="/changeUnit".
   * @param {Event} event
   */
  changeUnit(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const row = select.closest('.napvila-bundle__unit');
    if (!(row instanceof HTMLElement)) return;
    const index = Number(row.dataset.unit) || 0;

    if (index === 0) {
      // Row #1 is the main selection: move the main picker so price and gallery follow.
      this.#selectInMainPicker(select.dataset.optionName || '', select.value);
    } else {
      this.#touched.add(index);
    }

    this.#renderUnits();
  }

  /**
   * Called from the add-on checkbox: on:change="/toggleAddon".
   * @param {Event} event
   */
  toggleAddon(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    input.closest('.napvila-bundle__addon')?.classList.toggle('napvila-bundle__addon--active', input.checked);
    this.#toggleExpressCheckout();
  }

  /**
   * Intercepts the product form submit when extra lines are needed (rows with a
   * different color/size, or the add-on): those lines are added first, then the
   * original submit is replayed for row #1 so Horizon's own handler adds it and
   * refreshes the cart drawer with everything.
   * @param {SubmitEvent} event
   */
  #onFormSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const productForm = this.#productForm;
    if (!productForm || !productForm.contains(form)) return;

    if (this.#bypassSubmit) {
      this.#bypassSubmit = false;
      return;
    }

    const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    const mainId = Number(idInput?.value);

    /** @type {Map<number, number>} */
    const lines = new Map();
    let mainQuantity = this.#quantity;

    if (this.#unitsActive && mainId) {
      for (const [index, row] of this.#rows.entries()) {
        if (index >= this.#quantity) break;
        const variant = index === 0 ? null : this.#rowVariant(row);
        const id = variant?.id || mainId;
        lines.set(id, (lines.get(id) || 0) + 1);
      }
      mainQuantity = lines.get(mainId) || 0;
      lines.delete(mainId);
    }

    const addon = this.refs.addon;
    if (addon?.checked) {
      const addonId = Number(addon.dataset.variantId);
      if (addonId) lines.set(addonId, (lines.get(addonId) || 0) + (Number(addon.dataset.quantity) || 1));
    }

    if (lines.size === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      const items = [...lines].map(([id, quantity]) => ({ id, quantity }));
      const body = JSON.stringify({ items });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body }),
      });
      if (!response.ok) console.error('napvila bundle: could not add', await response.text());
    } catch (error) {
      console.error('napvila bundle:', error);
    }

    const selector = /** @type {any} */ (this.#quantitySelector);

    // Row #1 is always the main variant, so mainQuantity is at least 1 here.
    // The submit event (and Horizon's FormData read) runs synchronously inside
    // requestSubmit, so the quantity can be put back straight after.
    selector?.setValue?.(String(mainQuantity));
    this.#bypassSubmit = true;
    form.requestSubmit();
    selector?.setValue?.(String(this.#quantity));
  };

  /** @param {Event & { detail?: { quantity?: number } }} event */
  #onQuantityUpdate = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    const quantity = Number(event.detail?.quantity);
    if (!Number.isFinite(quantity)) return;
    this.#highlight(quantity, true);
  };

  /** @param {any} event */
  #onProductSelect = (event) => {
    event.promise
      ?.then((/** @type {any} */ result) => {
        const variant = result?.detail?.resource;
        if (!variant || !Array.isArray(variant.options)) return;
        this.#mainOptions = variant.options;
        this.#mainPrice = Number(variant.price) || this.#mainPrice;
        this.#renderUnits();
      })
      .catch(() => {});
  };

  #syncFromForm() {
    const selector = /** @type {any} */ (this.#quantitySelector);
    const quantity = Number(selector?.getValue?.());
    this.#highlight(Number.isFinite(quantity) && quantity > 0 ? quantity : 1, true);
  }

  /**
   * @param {number} quantity
   * @param {boolean} setChecked - Whether to move the checked radio to the matching tier.
   */
  #highlight(quantity, setChecked) {
    for (const radio of this.refs.radios) {
      const matches = Number(radio.value) === quantity;
      if (setChecked) radio.checked = matches;
      radio.closest('.napvila-bundle__tier')?.classList.toggle('napvila-bundle__tier--active', radio.checked);
    }
    this.#quantity = quantity;
    this.#renderUnits();
  }

  get #unitsActive() {
    return Boolean(this.refs.units) && this.#rows.length >= this.#quantity && this.#quantity > 1;
  }

  /** The options currently chosen in the main picker. @returns {string[] | null} */
  #readMainOptions() {
    if (this.#mainOptions) return this.#mainOptions;
    const id = Number(
      /** @type {HTMLInputElement | null} */ (this.#productForm?.querySelector('input[name="id"]'))?.value
    );
    const variant = this.#variants.find((v) => v.id === id);
    if (variant) {
      this.#mainOptions = variant.options;
      this.#mainPrice = variant.price;
    }
    return this.#mainOptions;
  }

  /**
   * @param {HTMLElement} row
   * @returns {Variant | undefined}
   */
  #rowVariant(row) {
    const values = [...row.querySelectorAll('select')]
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((select) => select.value);
    return this.#variants.find((variant) => variant.options.every((value, i) => value === values[i]));
  }

  /**
   * @param {HTMLElement} row
   * @param {string[]} options
   */
  #setRowOptions(row, options) {
    for (const select of row.querySelectorAll('select')) {
      const value = options[Number(select.dataset.optionIndex)];
      if (value !== undefined && select.value !== value) select.value = value;
    }
  }

  #renderUnits() {
    const units = this.refs.units;
    if (!units) return;

    const active = this.#unitsActive;
    units.hidden = !active;

    const mainOptions = this.#readMainOptions();

    for (const [index, row] of this.#rows.entries()) {
      row.hidden = !active || index >= this.#quantity;
      if (mainOptions && (index === 0 || !this.#touched.has(index))) this.#setRowOptions(row, mainOptions);

      const colorSelect = /** @type {HTMLSelectElement | null} */ (row.querySelector('select[data-color]'));
      const hex = colorSelect?.selectedOptions[0]?.dataset.hex?.trim();
      if (hex) row.style.setProperty('--nv-unit-color', hex);
    }

    this.#renderPrices(active);
    this.#toggleExpressCheckout();
  }

  /**
   * "Buy with Shop" and other express buttons skip the cart, so they would only
   * buy row #1 × quantity. Hide them while the order needs more than one line.
   */
  #toggleExpressCheckout() {
    let needsLines = Boolean(this.refs.addon?.checked);
    if (!needsLines && this.#unitsActive) {
      const rows = this.#rows.slice(0, this.#quantity);
      const first = rows[0] && this.#rowVariant(rows[0])?.id;
      needsLines = rows.some((row) => this.#rowVariant(row)?.id !== first);
    }
    this.#productForm?.classList.toggle('napvila-bundle--multi-line', needsLines);
  }

  /** Keeps the tier totals true to the chosen sizes (sizes have different prices). */
  #renderPrices(unitsActive) {
    const unitPrice = this.#mainPrice;
    if (!unitPrice) return;

    let rowsTotal = 0;
    if (unitsActive) {
      for (const [index, row] of this.#rows.entries()) {
        if (index >= this.#quantity) break;
        rowsTotal += (index === 0 ? unitPrice : this.#rowVariant(row)?.price) || unitPrice;
      }
    }

    for (const radio of this.refs.radios) {
      const tier = radio.closest('.napvila-bundle__tier');
      if (!tier) continue;
      const quantity = Number(radio.value) || 1;
      const percent = Number(radio.dataset.percent) || 0;
      const useRows = unitsActive && radio.checked && quantity === this.#quantity;
      const compare = useRows ? rowsTotal : unitPrice * quantity;
      // Same integer maths as the Liquid that rendered the tiers.
      const total = Math.floor((compare * (100 - percent)) / 100);
      const each = Math.floor(total / quantity);
      const mixed = useRows && rowsTotal !== unitPrice * quantity;

      const sub = tier.querySelector('.napvila-bundle__sub');
      if (sub) {
        const eachText = (this.dataset.eachTemplate || '[price] each').replace('[price]', this.#money(each));
        const parts = mixed ? [`${quantity} ×`] : [`${quantity} × ${this.#money(unitPrice)}`];
        if (percent > 0 || mixed) parts.push(eachText);
        sub.textContent = parts.join(mixed ? ' ' : ' · ');
      }

      const totalEl = tier.querySelector('.napvila-bundle__total');
      if (totalEl) totalEl.textContent = this.#money(total);
      const compareEl = tier.querySelector('.napvila-bundle__compare');
      if (compareEl) compareEl.textContent = this.#money(compare);
      const savingEl = tier.querySelector('.napvila-bundle__saving');
      if (savingEl) {
        savingEl.textContent = (this.dataset.saveTemplate || 'Save [amount]').replace('[amount]', this.#money(compare - total));
      }
    }
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  /**
   * Selects an option value in the main variant picker (buttons/swatches or dropdowns).
   * @param {string} optionName
   * @param {string} value
   */
  #selectInMainPicker(optionName, value) {
    const picker = this.#variantPicker;
    if (!picker) return;

    for (const input of picker.querySelectorAll('input[type="radio"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (input.dataset.optionName === optionName && input.value === value) {
        if (!input.checked) input.click();
        return;
      }
    }

    for (const select of picker.querySelectorAll('select')) {
      const option = [...select.options].find((o) => o.dataset.optionName === optionName && o.value === value);
      if (option && !option.selected) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
  }
}

if (!customElements.get('napvila-bundle-picker-component')) {
  customElements.define('napvila-bundle-picker-component', NapvilaBundlePickerComponent);
}
